/**
 * Improved image compression with better size targeting
 */
export interface CompressionOptions {
  /** Maximum width or height (maintains aspect ratio) */
  maxSize?: number;
  /** Quality level (0.0 to 1.0) - lower for smaller files */
  quality?: number;
  /** Target maximum file size in MB */
  targetSizeMB?: number;
}

export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<File> => {
  const { maxSize = 1200, quality = 0.7, targetSizeMB = 1 } = options;
  void quality;

  if (!file.type.startsWith("image/")) {
    throw new Error("File is not an image");
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas context not available"));
      return;
    }

    img.onload = async () => {
      try {
        const targetSizeBytes = targetSizeMB * 1024 * 1024;
        let bestBlob: Blob | null = null;
        let bestFile: File | null = null;

        // Try multiple dimension/quality combinations
        const dimensionScales = [0.8, 0.6, 0.4, 0.3]; // More aggressive scaling
        void dimensionScales;
        const qualityLevels = [0.8, 0.6, 0.4, 0.3, 0.2, 0.1, 0.05]; // Much lower qualities

        // Start with the provided maxSize, then try smaller dimensions
        const maxDimensions = [
          maxSize,
          maxSize * 0.8,
          maxSize * 0.6,
          maxSize * 0.4,
        ];

        for (const maxDim of maxDimensions) {
          // Calculate dimensions
          let { width, height } = img;
          const scaleFactor = Math.min(maxDim / Math.max(width, height), 1);
          width = Math.round(width * scaleFactor);
          height = Math.round(height * scaleFactor);

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw image
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Try different quality levels for this dimension
          for (const qual of qualityLevels) {
            // Create a safe closure by capturing the current quality value
            const currentQuality = qual;
            const blob = await new Promise<Blob | null>((resolveBlob) => {
              canvas.toBlob(resolveBlob, "image/webp", currentQuality);
            });

            if (blob && blob.size <= targetSizeBytes) {
              // Found a combination that works!
              const fileName = file.name.replace(/\.[^/.]+$/, ".webp");
              const compressedFile = new File([blob], fileName, {
                type: "image/webp",
                lastModified: Date.now(),
              });

              console.log("Compression successful:", {
                original: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                compressed: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
                reduction: `${((1 - blob.size / file.size) * 100).toFixed(1)}%`,
                dimensions: `${width}x${height}`,
                quality: currentQuality,
              });

              resolve(compressedFile);
              return;
            }

            // Keep track of the smallest file we've created so far
            if (!bestBlob || (blob && blob.size < bestBlob.size)) {
              bestBlob = blob;
              if (blob) {
                const fileName = file.name.replace(/\.[^/.]+$/, ".webp");
                bestFile = new File([blob], fileName, {
                  type: "image/webp",
                  lastModified: Date.now(),
                });
              }
            }
          }
        }

        // If we couldn't hit the target, return the smallest file we created
        if (bestFile) {
          console.log(
            "Could not reach target size, returning best compression:",
            {
              original: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
              compressed: `${(bestFile.size / 1024 / 1024).toFixed(2)} MB`,
              reduction: `${((1 - bestFile.size / file.size) * 100).toFixed(1)}%`,
              target: `${targetSizeMB} MB`,
            }
          );
          resolve(bestFile);
        } else {
          reject(new Error("Failed to compress image"));
        }
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

// Alternative: Simple aggressive compression that prioritizes hitting the target
export const aggressiveCompressImage = async (
  file: File,
  targetSizeMB: number = 1
): Promise<File> => {
  if (!file.type.startsWith("image/")) {
    throw new Error("File is not an image");
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context not available"));
        return;
      }

      const targetBytes = targetSizeMB * 1024 * 1024;

      // Start with very aggressive settings
      let scale = 0.5; // Start with 50% of original dimensions
      let quality = 0.3; // Start with low quality

      while (scale > 0.1 && quality > 0.05) {
        // Calculate new dimensions
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);

        canvas.width = width;
        canvas.height = height;

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Create a safe closure by capturing the current quality value
        const currentQuality = quality;
        const blob = await new Promise<Blob | null>((resolveBlob) => {
          canvas.toBlob(resolveBlob, "image/webp", currentQuality);
        });

        if (blob && blob.size <= targetBytes) {
          const fileName = file.name.replace(/\.[^/.]+$/, ".webp");
          const compressedFile = new File([blob], fileName, {
            type: "image/webp",
            lastModified: Date.now(),
          });

          console.log("Aggressive compression successful:", {
            original: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
            compressed: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
            reduction: `${((1 - blob.size / file.size) * 100).toFixed(1)}%`,
            dimensions: `${width}x${height}`,
            scale: scale,
            quality: quality,
          });

          resolve(compressedFile);
          return;
        }

        // Reduce quality first, then scale if needed
        if (quality > 0.1) {
          quality -= 0.05;
        } else {
          scale -= 0.1;
          quality = 0.3; // Reset quality when reducing scale
        }
      }

      reject(new Error(`Could not compress image to ${targetSizeMB}MB`));
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
};
